import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CalendarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChartPieIcon,
  UsersIcon,
  RocketLaunchIcon,
  BoltIcon,
  PresentationChartLineIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const styles = {
  container: {
    padding: '0',
    backgroundColor: 'transparent',
    minHeight: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginTop: '4px'
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none'
  },
  btnPrimary: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white'
  },
  btnSecondary: {
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-color)'
  },
  btnIcon: {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  select: {
    padding: '10px 36px 10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '16px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  statCard: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid var(--border-color)'
  },
  statHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statTrend: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: '12px'
  },
  statTrendUp: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981'
  },
  statTrendDown: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444'
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: '14px',
    color: 'var(--text-secondary)'
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
    marginBottom: '24px'
  },
  card: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)'
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0
  },
  cardBody: {
    padding: '20px'
  },
  chartContainer: {
    height: '300px',
    position: 'relative'
  },
  chartPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  barChart: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '100%',
    padding: '20px 0'
  },
  bar: {
    width: '40px',
    borderRadius: '4px 4px 0 0',
    backgroundColor: 'var(--accent-primary)',
    transition: 'height 0.3s ease',
    position: 'relative'
  },
  barLabel: {
    position: 'absolute',
    bottom: '-24px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap'
  },
  pieChartContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  pieChart: {
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    position: 'relative'
  },
  pieLegend: {
    marginLeft: '24px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px'
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '3px'
  },
  legendLabel: {
    color: 'var(--text-secondary)'
  },
  legendValue: {
    marginLeft: 'auto',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-primary)'
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-color)'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  statusSuccess: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981'
  },
  statusFailed: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444'
  },
  statusPending: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#f59e0b'
  },
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  metricItem: {
    textAlign: 'center',
    padding: '16px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '8px'
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  },
  metricLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    marginTop: '4px'
  },
  heatmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginTop: '16px'
  },
  heatmapCell: {
    aspectRatio: '1',
    borderRadius: '4px',
    minHeight: '30px'
  },
  heatmapLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    textAlign: 'center',
    padding: '4px'
  },
  timelineItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--border-color)'
  },
  timelineDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginTop: '4px',
    flexShrink: 0
  },
  timelineContent: {
    flex: 1
  },
  timelineTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '4px'
  },
  timelineTime: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  },
  progressBar: {
    height: '8px',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  geoMap: {
    height: '250px',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    flexDirection: 'column',
    gap: '12px'
  },
  deviceBreakdown: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center'
  },
  deviceItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  deviceIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-tertiary)'
  },
  devicePercent: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  deviceLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: 'var(--text-secondary)'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite'
  }
};

const DeploymentAnalyticsDashboard = ({ projectId, deploymentId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [selectedDeployment, setSelectedDeployment] = useState(deploymentId || 'all');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_range: dateRange,
        deployment_id: selectedDeployment !== 'all' ? selectedDeployment : undefined
      };

      const [analyticsRes, deploymentsRes] = await Promise.all([
        api.get(`/content/analytics${projectId ? `?project_id=${projectId}` : ''}`, { params }),
        api.get('/content/deployments')
      ]);

      setAnalytics(analyticsRes.data || generateMockAnalytics());
      setDeployments(deploymentsRes.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(generateMockAnalytics());
    } finally {
      setLoading(false);
    }
  }, [projectId, dateRange, selectedDeployment]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const generateMockAnalytics = () => ({
    overview: {
      totalDeployments: 156,
      successRate: 94.2,
      avgDeployTime: '2m 34s',
      activeDeployments: 12,
      totalViews: 45230,
      totalClicks: 3420,
      conversionRate: 7.6,
      bounceRate: 42.3
    },
    trends: {
      views: [1200, 1500, 1800, 2100, 1900, 2400, 2800, 3200, 2900, 3100, 3400, 3800],
      clicks: [90, 120, 150, 180, 160, 200, 240, 280, 260, 290, 320, 360],
      conversions: [12, 18, 22, 28, 24, 32, 38, 45, 42, 48, 52, 58]
    },
    deviceBreakdown: {
      desktop: 58,
      mobile: 35,
      tablet: 7
    },
    topContent: [
      { id: 1, name: 'Summer Sale Landing Page', views: 12500, clicks: 890, conversion: 8.2 },
      { id: 2, name: 'Product Launch Email', views: 8900, clicks: 620, conversion: 6.9 },
      { id: 3, name: 'Newsletter Q3', views: 7200, clicks: 480, conversion: 5.4 },
      { id: 4, name: 'Holiday Campaign', views: 6100, clicks: 410, conversion: 7.1 },
      { id: 5, name: 'Welcome Series #1', views: 5400, clicks: 380, conversion: 9.2 }
    ],
    recentDeployments: [
      { id: 1, name: 'Blog Post: AI Marketing', status: 'success', time: '10 min ago', views: 234 },
      { id: 2, name: 'Landing Page v2.3', status: 'success', time: '1 hour ago', views: 1820 },
      { id: 3, name: 'Email Campaign', status: 'failed', time: '2 hours ago', views: 0 },
      { id: 4, name: 'Social Media Post', status: 'success', time: '3 hours ago', views: 890 },
      { id: 5, name: 'Product Update', status: 'pending', time: '4 hours ago', views: 0 }
    ],
    geoData: [
      { country: 'United States', views: 18500, percent: 41 },
      { country: 'United Kingdom', views: 7200, percent: 16 },
      { country: 'Germany', views: 5400, percent: 12 },
      { country: 'France', views: 4100, percent: 9 },
      { country: 'Canada', views: 3600, percent: 8 }
    ],
    hourlyHeatmap: Array.from({ length: 24 }, () => Math.floor(Math.random() * 100))
  });

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      success: { ...styles.statusBadge, ...styles.statusSuccess },
      failed: { ...styles.statusBadge, ...styles.statusFailed },
      pending: { ...styles.statusBadge, ...styles.statusPending }
    };
    const icons = {
      success: <CheckCircleIcon style={{ width: '14px', height: '14px' }} />,
      failed: <XCircleIcon style={{ width: '14px', height: '14px' }} />,
      pending: <ClockIcon style={{ width: '14px', height: '14px' }} />
    };
    return (
      <span style={statusStyles[status] || statusStyles.pending}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getHeatmapColor = (value) => {
    if (value > 80) return 'rgba(16, 185, 129, 0.8)';
    if (value > 60) return 'rgba(16, 185, 129, 0.6)';
    if (value > 40) return 'rgba(16, 185, 129, 0.4)';
    if (value > 20) return 'rgba(16, 185, 129, 0.2)';
    return 'var(--bg-tertiary)';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <ArrowPathIcon style={{ ...styles.spinner, color: 'var(--accent-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Filter and Actions Bar */}
      <div style={styles.header}>
        <div style={styles.headerActions}>
          <select
            style={styles.select}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <select
            style={styles.select}
            value={selectedDeployment}
            onChange={(e) => setSelectedDeployment(e.target.value)}
          >
            <option value="all">All Deployments</option>
            {deployments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button style={styles.btnIcon} onClick={fetchAnalytics}>
            <ArrowPathIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
          </button>
          <button style={{ ...styles.btn, ...styles.btnSecondary }}>
            <ArrowDownTrayIcon style={{ width: '18px', height: '18px' }} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
              <RocketLaunchIcon style={{ width: '20px', height: '20px', color: '#6366f1' }} />
            </div>
            <span style={{ ...styles.statTrend, ...styles.statTrendUp }}>
              <ArrowTrendingUpIcon style={{ width: '14px', height: '14px' }} />
              12%
            </span>
          </div>
          <div style={styles.statValue}>{analytics.overview.totalDeployments}</div>
          <div style={styles.statLabel}>Total Deployments</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
              <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#10b981' }} />
            </div>
            <span style={{ ...styles.statTrend, ...styles.statTrendUp }}>
              <ArrowTrendingUpIcon style={{ width: '14px', height: '14px' }} />
              3%
            </span>
          </div>
          <div style={styles.statValue}>{analytics.overview.successRate}%</div>
          <div style={styles.statLabel}>Success Rate</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
              <EyeIcon style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
            </div>
            <span style={{ ...styles.statTrend, ...styles.statTrendUp }}>
              <ArrowTrendingUpIcon style={{ width: '14px', height: '14px' }} />
              28%
            </span>
          </div>
          <div style={styles.statValue}>{formatNumber(analytics.overview.totalViews)}</div>
          <div style={styles.statLabel}>Total Views</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
              <CursorArrowRaysIcon style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
            </div>
            <span style={{ ...styles.statTrend, ...styles.statTrendDown }}>
              <ArrowTrendingDownIcon style={{ width: '14px', height: '14px' }} />
              5%
            </span>
          </div>
          <div style={styles.statValue}>{analytics.overview.conversionRate}%</div>
          <div style={styles.statLabel}>Conversion Rate</div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={styles.chartsGrid}>
        {/* Performance Trend */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Performance Trend</h3>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['Views', 'Clicks', 'Conversions'].map((metric, i) => (
                <span key={metric} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: ['#6366f1', '#10b981', '#f59e0b'][i] }} />
                  {metric}
                </span>
              ))}
            </div>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.chartContainer}>
              <div style={styles.barChart}>
                {analytics.trends.views.map((value, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <div
                      style={{
                        ...styles.bar,
                        height: `${(value / 4000) * 100}%`,
                        backgroundColor: '#6366f1',
                        opacity: 0.6
                      }}
                    />
                    <span style={styles.barLabel}>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Device Breakdown */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Device Breakdown</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.deviceBreakdown}>
              {[
                { icon: ComputerDesktopIcon, label: 'Desktop', value: analytics.deviceBreakdown.desktop },
                { icon: DevicePhoneMobileIcon, label: 'Mobile', value: analytics.deviceBreakdown.mobile },
                { icon: DocumentTextIcon, label: 'Tablet', value: analytics.deviceBreakdown.tablet }
              ].map(device => (
                <div key={device.label} style={styles.deviceItem}>
                  <div style={styles.deviceIcon}>
                    <device.icon style={{ width: '24px', height: '24px', color: 'var(--accent-primary)' }} />
                  </div>
                  <span style={styles.devicePercent}>{device.value}%</span>
                  <span style={styles.deviceLabel}>{device.label}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '24px' }}>
              <div style={styles.progressBar}>
                <div style={{ display: 'flex', height: '100%' }}>
                  <div style={{ ...styles.progressFill, width: `${analytics.deviceBreakdown.desktop}%`, backgroundColor: '#6366f1' }} />
                  <div style={{ ...styles.progressFill, width: `${analytics.deviceBreakdown.mobile}%`, backgroundColor: '#10b981' }} />
                  <div style={{ ...styles.progressFill, width: `${analytics.deviceBreakdown.tablet}%`, backgroundColor: '#f59e0b' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content & Geography Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Top Performing Content */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Top Performing Content</h3>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Content</th>
                  <th style={styles.th}>Views</th>
                  <th style={styles.th}>Clicks</th>
                  <th style={styles.th}>Conv.</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topContent.map(content => (
                  <tr key={content.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '500' }}>{content.name}</div>
                    </td>
                    <td style={styles.td}>{formatNumber(content.views)}</td>
                    <td style={styles.td}>{formatNumber(content.clicks)}</td>
                    <td style={styles.td}>
                      <span style={{ color: '#10b981', fontWeight: '500' }}>{content.conversion}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Geographic Distribution */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Geographic Distribution</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.geoMap}>
              <GlobeAltIcon style={{ width: '48px', height: '48px', opacity: 0.5 }} />
              <span>Geographic map visualization</span>
            </div>
            <div style={{ marginTop: '16px' }}>
              {analytics.geoData.map(geo => (
                <div key={geo.country} style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <MapPinIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)', marginRight: '8px' }} />
                  <span style={{ flex: 1, fontSize: '14px', color: 'var(--text-primary)' }}>{geo.country}</span>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)', marginRight: '12px' }}>{formatNumber(geo.views)}</span>
                  <div style={{ width: '100px' }}>
                    <div style={styles.progressBar}>
                      <div style={{ ...styles.progressFill, width: `${geo.percent}%`, backgroundColor: 'var(--accent-primary)' }} />
                    </div>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px', width: '40px' }}>{geo.percent}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Deployments & Activity Heatmap */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Recent Deployments */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Recent Deployments</h3>
            <button style={{ ...styles.btn, ...styles.btnSecondary, padding: '6px 12px', fontSize: '12px' }}>
              View All
            </button>
          </div>
          <div style={styles.cardBody}>
            {analytics.recentDeployments.map(deployment => (
              <div key={deployment.id} style={styles.timelineItem}>
                <div
                  style={{
                    ...styles.timelineDot,
                    backgroundColor: deployment.status === 'success' ? '#10b981' : deployment.status === 'failed' ? '#ef4444' : '#f59e0b'
                  }}
                />
                <div style={styles.timelineContent}>
                  <div style={styles.timelineTitle}>{deployment.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={styles.timelineTime}>{deployment.time}</span>
                    {getStatusBadge(deployment.status)}
                    {deployment.views > 0 && (
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        <EyeIcon style={{ width: '12px', height: '12px', display: 'inline', marginRight: '4px' }} />
                        {formatNumber(deployment.views)} views
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Heatmap */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Activity Heatmap</h3>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Hourly distribution</span>
          </div>
          <div style={styles.cardBody}>
            <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              {['12am', '6am', '12pm', '6pm', '12am'].map(time => (
                <span key={time} style={{ flex: 1, textAlign: 'center' }}>{time}</span>
              ))}
            </div>
            <div style={styles.heatmapGrid}>
              {analytics.hourlyHeatmap.map((value, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.heatmapCell,
                    backgroundColor: getHeatmapColor(value)
                  }}
                  title={`${index}:00 - ${value}% activity`}
                />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Low</span>
                {[0.2, 0.4, 0.6, 0.8].map(opacity => (
                  <div
                    key={opacity}
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '3px',
                      backgroundColor: `rgba(16, 185, 129, ${opacity})`
                    }}
                  />
                ))}
                <span style={{ color: 'var(--text-secondary)' }}>High</span>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Peak: 2pm - 4pm
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div style={{ ...styles.card, marginTop: '24px' }}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Performance Metrics</h3>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.metricsRow}>
            {[
              { label: 'Avg. Deploy Time', value: analytics.overview.avgDeployTime, icon: ClockIcon },
              { label: 'Active Deployments', value: analytics.overview.activeDeployments, icon: BoltIcon },
              { label: 'Bounce Rate', value: `${analytics.overview.bounceRate}%`, icon: ExclamationTriangleIcon },
              { label: 'Total Clicks', value: formatNumber(analytics.overview.totalClicks), icon: CursorArrowRaysIcon },
              { label: 'Avg. Session', value: '3m 42s', icon: ClockIcon },
              { label: 'Pages/Session', value: '4.2', icon: DocumentTextIcon }
            ].map(metric => (
              <div key={metric.label} style={styles.metricItem}>
                <metric.icon style={{ width: '24px', height: '24px', color: 'var(--accent-primary)', marginBottom: '8px' }} />
                <div style={styles.metricValue}>{metric.value}</div>
                <div style={styles.metricLabel}>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentAnalyticsDashboard;
